// Reverse side of the structured relationship layer: given a concept, show the
// works that carry it. Relationships come from the codex_links table, so the
// internal-link graph is edited as data rather than hard-coded per page.
import { Link } from "@tanstack/react-router";
import type { CodexLink } from "@/lib/codex";
import { track } from "@/lib/analytics";

/** Entity kinds that resolve to a public canonical work URL. */
const WORK_KINDS: Record<string, { label: string; to: string; param: string }> = {
  release: { label: "Release", to: "/music/$slug", param: "slug" },
  book: { label: "Book", to: "/books/$slug", param: "slug" },
  wisdom: { label: "Wisdom", to: "/wisdom/$slug", param: "slug" },
  activation: { label: "Activation", to: "/activations/$slug", param: "slug" },
};

export function conceptWorkLinks(links: CodexLink[]): CodexLink[] {
  return links.filter((l) => l.entity_kind in WORK_KINDS);
}

export function ConceptWorks({ links, conceptSlug }: { links: CodexLink[]; conceptSlug: string }) {
  const works = conceptWorkLinks(links);
  if (works.length === 0) return null;

  return (
    <section className="mt-12" aria-labelledby="concept-works">
      <h2 id="concept-works" className="font-serif text-2xl text-gold-luminous">
        Works that carry this concept
      </h2>
      <ul className="mt-4 grid gap-3 sm:grid-cols-2">
        {works.map((l) => {
          const kind = WORK_KINDS[l.entity_kind]!;
          return (
            <li key={`${l.entity_kind}:${l.entity_ref}`}>
              <Link
                to={kind.to}
                params={{ [kind.param]: l.entity_ref } as never}
                onClick={() =>
                  track("concept_relation_click", {
                    concept: conceptSlug,
                    entity_kind: l.entity_kind,
                    entity_ref: l.entity_ref,
                  })
                }
                className="group flex h-full flex-col gap-1.5 rounded-xl border hairline bg-obsidian/60 p-5 backdrop-blur transition-all hover:border-gold hover:bg-obsidian/80"
              >
                <span className="text-[10px] uppercase tracking-[0.3em] text-gold">{kind.label}</span>
                <span className="font-serif text-lg text-ivory group-hover:text-gold-luminous">
                  {l.label ?? l.entity_ref}
                </span>
              </Link>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
